import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sidebar } from './AdminDashboard';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminAvaliacoes() {
  const { token, user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedAvaliacao, setSelectedAvaliacao] = useState(null);

  // Filters
  const [filtros, setFiltros] = useState({
    busca: '',
    positiva: '',
    dataInicio: '',
    dataFim: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchAvaliacoes();
  }, [isAuthenticated, navigate, page, filtros]);

  const fetchAvaliacoes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 15,
        ...filtros
      });
      const response = await axios.get(`${API_URL}/admin/avaliacoes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvaliacoes(response.data.avaliacoes);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Erro ao carregar avaliações:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const exportData = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `avaliacoes_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (err) {
      console.error('Erro ao exportar:', err);
      alert('Erro ao exportar dados');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="admin-layout">
      <Sidebar user={user} logout={logout} navigate={navigate} active="avaliacoes" />
      <main className="admin-content">
        <div className="admin-header">
          <h1>Avaliações</h1>
          <button className="btn btn-primary" onClick={exportData}>
            📥 Exportar JSON
          </button>
        </div>

        {/* Filters */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="filters">
            <input
              type="text"
              name="busca"
              placeholder="Buscar por nome, email ou telefone..."
              value={filtros.busca}
              onChange={handleFilterChange}
              style={{ flex: 2 }}
            />
            <select
              name="positiva"
              value={filtros.positiva}
              onChange={handleFilterChange}
            >
              <option value="">Todas</option>
              <option value="true">Positivas</option>
              <option value="false">Negativas</option>
            </select>
            <input
              type="date"
              name="dataInicio"
              value={filtros.dataInicio}
              onChange={handleFilterChange}
              placeholder="Data início"
            />
            <input
              type="date"
              name="dataFim"
              value={filtros.dataFim}
              onChange={handleFilterChange}
              placeholder="Data fim"
            />
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          <div className="table-header">
            <h2>{total} avaliações encontradas</h2>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>WhatsApp</th>
                    <th>Data</th>
                    <th>Nota</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {avaliacoes.map(av => (
                    <tr key={av.id}>
                      <td>{av.nome}</td>
                      <td>{av.email}</td>
                      <td>{av.whatsapp}</td>
                      <td>{new Date(av.created_at).toLocaleDateString('pt-BR')}</td>
                      <td>
                        <strong style={{ color: av.nota_geral >= 7 ? 'var(--success-color)' : av.nota_geral >= 5 ? 'var(--warning-color)' : 'var(--error-color)' }}>
                          {av.nota_geral}/10
                        </strong>
                      </td>
                      <td>
                        <span className={`badge ${av.avaliacao_positiva ? 'badge-success' : 'badge-error'}`}>
                          {av.avaliacao_positiva ? 'Positiva' : 'Negativa'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                          onClick={() => setSelectedAvaliacao(av)}
                        >
                          Ver detalhes
                        </button>
                      </td>
                    </tr>
                  ))}
                  {avaliacoes.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
                        Nenhuma avaliação encontrada
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Anterior
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={page === pageNum ? 'active' : ''}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Próximo
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Modal */}
        {selectedAvaliacao && (
          <AvaliacaoModal
            avaliacao={selectedAvaliacao}
            onClose={() => setSelectedAvaliacao(null)}
          />
        )}
      </main>
    </div>
  );
}

function AvaliacaoModal({ avaliacao, onClose }) {
  const formatValue = (value) => {
    const map = {
      'otimo': 'Ótimo',
      'bom': 'Bom',
      'regular': 'Regular',
      'ruim': 'Ruim',
      'sim_certeza': 'Sim, com certeza',
      'provavelmente_sim': 'Provavelmente sim',
      'talvez': 'Talvez',
      'provavelmente_nao': 'Provavelmente não',
      'nao': 'Não'
    };
    return map[value] || value || '-';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Detalhes da Avaliação</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {/* Info do Paciente */}
          <div className="detail-section">
            <h3>Informações do Paciente</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Nome</label>
                <span>{avaliacao.nome}</span>
              </div>
              <div className="detail-item">
                <label>Email</label>
                <span>{avaliacao.email}</span>
              </div>
              <div className="detail-item">
                <label>WhatsApp</label>
                <span>{avaliacao.whatsapp}</span>
              </div>
              <div className="detail-item">
                <label>Data da Avaliação</label>
                <span>{new Date(avaliacao.created_at).toLocaleString('pt-BR')}</span>
              </div>
              {avaliacao.data_internacao && (
                <div className="detail-item">
                  <label>Data Internação</label>
                  <span>{new Date(avaliacao.data_internacao).toLocaleDateString('pt-BR')}</span>
                </div>
              )}
              {avaliacao.quarto_leito && (
                <div className="detail-item">
                  <label>Quarto/Leito</label>
                  <span>{avaliacao.quarto_leito}</span>
                </div>
              )}
              {avaliacao.setor && (
                <div className="detail-item">
                  <label>Setor</label>
                  <span>{avaliacao.setor}</span>
                </div>
              )}
            </div>
          </div>

          {/* Resultado */}
          <div className="detail-section">
            <h3>Resultado</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Nota Geral</label>
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: avaliacao.nota_geral >= 7 ? 'var(--success-color)' : avaliacao.nota_geral >= 5 ? 'var(--warning-color)' : 'var(--error-color)' }}>
                  {avaliacao.nota_geral}/10
                </span>
              </div>
              <div className="detail-item">
                <label>Classificação</label>
                <span className={`badge ${avaliacao.avaliacao_positiva ? 'badge-success' : 'badge-error'}`}>
                  {avaliacao.avaliacao_positiva ? 'Positiva' : 'Negativa'}
                </span>
              </div>
              <div className="detail-item">
                <label>Recomendaria</label>
                <span>{formatValue(avaliacao.recomendaria)}</span>
              </div>
              <div className="detail-item">
                <label>Pontuação Calculada</label>
                <span>{avaliacao.pontuacao_calculada}</span>
              </div>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="detail-section">
            <h3>Status de Envio</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>WhatsApp Enviado</label>
                <span style={{ color: avaliacao.whatsapp_enviado ? 'var(--success-color)' : 'var(--text-secondary)' }}>
                  {avaliacao.whatsapp_enviado ? 'Sim' : 'Não'}
                </span>
              </div>
              <div className="detail-item">
                <label>Link Google Enviado</label>
                <span style={{ color: avaliacao.google_link_enviado ? 'var(--success-color)' : 'var(--text-secondary)' }}>
                  {avaliacao.google_link_enviado ? 'Sim' : 'Não'}
                </span>
              </div>
            </div>
          </div>

          {/* Recepção */}
          <div className="detail-section">
            <h3>1. Recepção e Admissão</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Cordialidade</label>
                <span>{formatValue(avaliacao.recepcao_cordialidade)}</span>
              </div>
              <div className="detail-item">
                <label>Agilidade</label>
                <span>{formatValue(avaliacao.recepcao_agilidade)}</span>
              </div>
              <div className="detail-item">
                <label>Clareza</label>
                <span>{formatValue(avaliacao.recepcao_clareza)}</span>
              </div>
            </div>
            {avaliacao.recepcao_outro && (
              <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                "{avaliacao.recepcao_outro}"
              </p>
            )}
          </div>

          {/* Enfermagem */}
          <div className="detail-section">
            <h3>2. Equipe de Enfermagem</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Cordialidade</label>
                <span>{formatValue(avaliacao.enfermagem_cordialidade)}</span>
              </div>
              <div className="detail-item">
                <label>Rapidez</label>
                <span>{formatValue(avaliacao.enfermagem_rapidez)}</span>
              </div>
              <div className="detail-item">
                <label>Cuidado c/ Medicamentos</label>
                <span>{formatValue(avaliacao.enfermagem_cuidado_medicamentos)}</span>
              </div>
              <div className="detail-item">
                <label>Orientações</label>
                <span>{formatValue(avaliacao.enfermagem_orientacoes)}</span>
              </div>
              <div className="detail-item">
                <label>Atenção</label>
                <span>{formatValue(avaliacao.enfermagem_atencao)}</span>
              </div>
            </div>
            {avaliacao.enfermagem_outro && (
              <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                "{avaliacao.enfermagem_outro}"
              </p>
            )}
          </div>

          {/* Estrutura */}
          <div className="detail-section">
            <h3>3. Estrutura e Acomodações</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Limpeza Quarto</label>
                <span>{formatValue(avaliacao.estrutura_limpeza_quarto)}</span>
              </div>
              <div className="detail-item">
                <label>Limpeza Banheiro</label>
                <span>{formatValue(avaliacao.estrutura_limpeza_banheiro)}</span>
              </div>
              <div className="detail-item">
                <label>Conforto Cama</label>
                <span>{formatValue(avaliacao.estrutura_conforto_cama)}</span>
              </div>
              <div className="detail-item">
                <label>Temperatura</label>
                <span>{formatValue(avaliacao.estrutura_temperatura)}</span>
              </div>
              <div className="detail-item">
                <label>Ruído</label>
                <span>{formatValue(avaliacao.estrutura_ruido)}</span>
              </div>
              <div className="detail-item">
                <label>Equipamentos</label>
                <span>{formatValue(avaliacao.estrutura_equipamentos)}</span>
              </div>
            </div>
            {avaliacao.estrutura_outro && (
              <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                "{avaliacao.estrutura_outro}"
              </p>
            )}
          </div>

          {/* Alimentação */}
          <div className="detail-section">
            <h3>4. Alimentação / Nutrição</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <label>Qualidade</label>
                <span>{formatValue(avaliacao.alimentacao_qualidade)}</span>
              </div>
              <div className="detail-item">
                <label>Temperatura</label>
                <span>{formatValue(avaliacao.alimentacao_temperatura)}</span>
              </div>
              <div className="detail-item">
                <label>Variedade</label>
                <span>{formatValue(avaliacao.alimentacao_variedade)}</span>
              </div>
              <div className="detail-item">
                <label>Apresentação</label>
                <span>{formatValue(avaliacao.alimentacao_apresentacao)}</span>
              </div>
              <div className="detail-item">
                <label>Pontualidade</label>
                <span>{formatValue(avaliacao.alimentacao_pontualidade)}</span>
              </div>
              <div className="detail-item">
                <label>Copeira</label>
                <span>{formatValue(avaliacao.alimentacao_atendimento_copeira)}</span>
              </div>
            </div>
            {avaliacao.alimentacao_outro && (
              <p style={{ marginTop: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                "{avaliacao.alimentacao_outro}"
              </p>
            )}
          </div>

          {/* Comentários */}
          <div className="detail-section">
            <h3>Comentários e Sugestões</h3>
            {avaliacao.profissional_destaque && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>Profissional Destaque:</label>
                <p style={{ color: 'var(--text-secondary)' }}>{avaliacao.profissional_destaque}</p>
              </div>
            )}
            {avaliacao.sugestao_melhoria && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>Sugestão de Melhoria:</label>
                <p style={{ color: 'var(--text-secondary)' }}>{avaliacao.sugestao_melhoria}</p>
              </div>
            )}
            {avaliacao.elogio_reclamacao && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>Elogio/Reclamação:</label>
                <p style={{ color: 'var(--text-secondary)' }}>{avaliacao.elogio_reclamacao}</p>
              </div>
            )}
            {avaliacao.avaliacao_geral_outro && (
              <div>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '5px' }}>Observações Gerais:</label>
                <p style={{ color: 'var(--text-secondary)' }}>{avaliacao.avaliacao_geral_outro}</p>
              </div>
            )}
            {!avaliacao.profissional_destaque && !avaliacao.sugestao_melhoria && !avaliacao.elogio_reclamacao && !avaliacao.avaliacao_geral_outro && (
              <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>Nenhum comentário adicional</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAvaliacoes;
